import { useRouter } from "@tanstack/react-router";
import { CheckCircle2, Clock, Loader2, RefreshCw, Users, XCircle } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface OwnerSyncRow {
  id: string;
  status: string;
  ownersCreated: number;
  ownersUpdated: number;
  ownersSkipped: number;
  ownersErrored: number;
  errorMessage: string | null;
  startedAt: string | number;
  completedAt: string | number | null;
}

function formatRelativeTime(ts: string | number | null): string {
  if (!ts) return "Never";
  const date = typeof ts === "number" ? new Date(ts * 1000) : new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function formatDuration(startedAt: string | number, completedAt: string | number | null): string {
  if (!completedAt) return "—";
  const start = typeof startedAt === "number" ? startedAt * 1000 : new Date(startedAt).getTime();
  const end =
    typeof completedAt === "number" ? completedAt * 1000 : new Date(completedAt).getTime();
  const ms = end - start;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

interface OwnerSyncSectionProps {
  initialHistory: OwnerSyncRow[];
}

export function OwnerSyncSection({ initialHistory }: OwnerSyncSectionProps) {
  const router = useRouter();
  const [history, setHistory] = useState(initialHistory);
  const [isSyncing, setIsSyncing] = useState(false);

  const lastSync = history[0] ?? null;

  const refreshData = useCallback(async () => {
    const res = await fetch("/api/hubspot/owners/sync-history");
    const data = await res.json();
    setHistory(data.results ?? []);
  }, []);

  async function handleSync() {
    setIsSyncing(true);
    try {
      const res = await fetch("/api/hubspot/owners/sync", { method: "POST" });
      const data = await res.json();
      if (data.status === "success") {
        toast.success("Owner sync complete", {
          description: `${data.ownersCreated} created, ${data.ownersUpdated} updated, ${data.ownersSkipped} skipped`,
        });
      } else {
        toast.error("Owner sync failed", { description: data.errorMessage ?? "Unknown error" });
      }
      await refreshData();
      router.invalidate();
    } catch {
      toast.error("Failed to sync owners");
    } finally {
      setIsSyncing(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Status cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Synced</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {lastSync ? formatRelativeTime(lastSync.completedAt ?? lastSync.startedAt) : "Never"}
            </div>
            <p className="text-xs text-muted-foreground">manual sync only</p>
          </CardContent>
        </Card>

        {lastSync && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Last Result</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {lastSync.ownersCreated + lastSync.ownersUpdated} owners
              </div>
              <p className="text-xs text-muted-foreground">
                {lastSync.status === "success" ? "completed successfully" : "failed"}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sync action */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium">Sync Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-lg border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10">
                <RefreshCw className="h-4 w-4 text-primary" />
              </div>
              <div>
                <span className="text-sm font-medium">Full Sync</span>
                <p className="text-xs text-muted-foreground">
                  Pull all owners from HubSpot and sync locally
                </p>
              </div>
            </div>
            <Button size="sm" onClick={handleSync} disabled={isSyncing}>
              {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sync history table */}
      <div>
        <h3 className="mb-3 text-sm font-medium">Sync History</h3>
        {history.length === 0 ? (
          <div className="rounded-md border py-8 text-center">
            <p className="text-sm text-muted-foreground">No syncs have been run yet.</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Created</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Updated</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Skipped</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Errored</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Duration</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {row.status === "success" ? (
                        <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Success
                        </span>
                      ) : (
                        <div>
                          <span className="flex items-center gap-1.5 text-sm text-destructive">
                            <XCircle className="h-3.5 w-3.5" />
                            Error
                          </span>
                          {row.errorMessage && (
                            <p
                              className="mt-0.5 text-xs text-muted-foreground max-w-[200px] truncate"
                              title={row.errorMessage}
                            >
                              {row.errorMessage}
                            </p>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{row.ownersCreated}</TableCell>
                    <TableCell className="text-sm">{row.ownersUpdated}</TableCell>
                    <TableCell className="text-sm">{row.ownersSkipped}</TableCell>
                    <TableCell className="text-sm">{row.ownersErrored}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDuration(row.startedAt, row.completedAt)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatRelativeTime(row.completedAt ?? row.startedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
